// src/components/exercise/ExerciseFooter.tsx
import React, { useState } from 'react';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore }   from '../../stores/useThemeStore';

interface ExerciseFooterProps {
  onPrev: () => void;
  onSubmit: () => void;
  onNext: () => void;
  canSubmit: boolean;
  isSubmitted: boolean;
  hasPrev: boolean;
  isLastExercise: boolean;
}

const ExerciseFooter: React.FC<ExerciseFooterProps> = ({
  onPrev, onSubmit, onNext, canSubmit, isSubmitted, hasPrev, isLastExercise,
}) => {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';
  const [prevHover,   setPrevHover]   = useState(false);
  const [actionHover, setActionHover] = useState(false);

  const actionLabel   = isSubmitted ? (isLastExercise ? 'Finish Section' : 'Next') : 'Submit Answer';
  const actionEnabled = isSubmitted || canSubmit;

  const disabledColor  = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)';
  const disabledBg     = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  return (
    <div style={{
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'space-between',
      padding:        '16px 24px',
      borderTop:      `1px solid ${C.border}`,
      background:     isDark ? 'rgba(13,18,32,0.8)' : 'rgba(248,250,252,0.95)',
      backdropFilter: 'blur(12px)',
      flexShrink:     0,
    }}>
      {/* Prev */}
      <button
        onClick={onPrev}
        disabled={!hasPrev}
        onMouseEnter={() => setPrevHover(true)}
        onMouseLeave={() => setPrevHover(false)}
        style={{
          padding:       '10px 20px',
          borderRadius:  '999px',
          border:        `1px solid ${hasPrev && prevHover ? C.borderH : C.border}`,
          background:    hasPrev && prevHover
            ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')
            : 'transparent',
          color:         hasPrev ? (prevHover ? C.text : C.muted) : disabledColor,
          fontSize:      '0.78rem',
          fontWeight:    700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          cursor:        hasPrev ? 'pointer' : 'not-allowed',
          transition:    'all 0.2s ease',
          opacity:       hasPrev ? 1 : 0.4,
          fontFamily:    'inherit',
        }}
      >
        ← Prev
      </button>

      {/* Submit / Next */}
      <button
        onClick={isSubmitted ? onNext : onSubmit}
        disabled={!actionEnabled}
        onMouseEnter={() => setActionHover(true)}
        onMouseLeave={() => setActionHover(false)}
        style={{
          padding:       '11px 28px',
          borderRadius:  '999px',
          border:        'none',
          background:    actionEnabled
            ? (isSubmitted
                ? (actionHover ? '#2fbd88' : C.success)
                : `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`)
            : disabledBg,
          color:         actionEnabled ? '#fff' : disabledColor,
          fontSize:      '0.82rem',
          fontWeight:    700,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          cursor:        actionEnabled ? 'pointer' : 'not-allowed',
          transition:    'all 0.2s ease',
          opacity:       actionEnabled ? 1 : 0.5,
          transform:     actionHover && actionEnabled ? 'translateY(-2px)' : 'none',
          boxShadow:     actionHover && actionEnabled
            ? (isSubmitted
                ? '0 6px 20px rgba(52,211,153,0.35)'
                : '0 6px 20px rgba(79,140,247,0.35)')
            : 'none',
          fontFamily:    'inherit',
        }}
      >
        {actionLabel} {isSubmitted && '→'}
      </button>
    </div>
  );
};

export default ExerciseFooter;
